import { getAssetUrl } from "./assets";
import { AVAILABLE_FONTS } from "./fonts";

/**
 * Automatically injects ALL CSS required by the package
 * This runs as a side effect when the package is imported
 * CSS is automatically bundled - no manual import needed!
 */

const FONT_FILES: Record<string, string> = {
	Amiri: "AmiriRegular.woff2",
	AmiriBold: "AmiriBold.woff2",
	Andalus: "Andalus.woff2",
	BaradaReqa: "BaradaReqa.woff2",
	FodaFreeFont: "FodaFreeFont.woff2",
	FreeMono: "FreeMono.woff2",
	FreeMonoBold: "FreeMonoBold.woff2",
	FreeSans: "FreeSans.woff2",
	FreeSansBold: "FreeSansBold.woff2",
	FreeSerif: "FreeSerif.woff2",
	FreeSerifBold: "FreeSerifBold.woff2",
	HSNOmar: "HSNOmar.woff2",
	KFGQPCUthmanTaha: "KFGQPCUthmanTaha.woff2",
	KSARegular: "KSARegular.woff2",
	mohammadboldart1: "mohammadboldart1.woff2",
	Monofonto: "Monofonto.woff2",
	NRTReg: "NRTReg.woff2",
	"SFSultan-Black": "SFSultan-Black.woff2",
	"STC-Regular": "STC-Regular.woff2",
};

/**
 * Generate font-face CSS (lazy - only called in browser)
 * This must be called client-side because it uses getAssetUrl()
 */
function generateFontFaceCSS(): string {
	// Only generate in browser (getAssetUrl is client-only)
	if (typeof window === "undefined") {
		return "";
	}

	return AVAILABLE_FONTS.map(({ value }) => {
		if (value === "default") {
			return "";
		}
		const file = FONT_FILES[value];
		if (!file) {
			return "";
		}
		const url = getAssetUrl(`fonts/${file}`);
		return `
@font-face {
	font-family: "${value}";
	src: url("${url}") format("woff2");
	font-display: swap;
	font-weight: 400;
	font-style: normal;
}
`;
	})
		.filter(Boolean)
		.join("\n");
}

// CSS variables (safe to compute at module load - no client functions)
const CSS_VARIABLES = `
/* React Prayer Widget - Auto-injected CSS */
:root {
	/* Updated to Islamic-themed dark color palette */
	--background: #1a1a1a;
	--foreground: #ffffff;
	--card: #111827;
	--card-foreground: #f9fafb;
	--popover: #111827;
	--popover-foreground: #ffffff;
	--primary: #0891b2;
	--primary-foreground: #ffffff;
	--secondary: #4b5563;
	--secondary-foreground: #f9fafb;
	--muted: #111827;
	--muted-foreground: #4b5563;
	--accent: #dc2626;
	--accent-foreground: #ffffff;
	--destructive: #dc2626;
	--destructive-foreground: #ffffff;
	--border: #4b5563;
	--input: #1f2937;
	--ring: #0891b2;
	--chart-1: #0891b2;
	--chart-2: #dc2626;
	--chart-3: #f59e0b;
	--chart-4: #4b5563;
	--chart-5: #ffffff;
	--radius: 0.5rem;
	--sidebar: #111827;
	--sidebar-foreground: #f9fafb;
	--sidebar-primary: #0891b2;
	--sidebar-primary-foreground: #ffffff;
	--sidebar-accent: #dc2626;
	--sidebar-accent-foreground: #ffffff;
	--sidebar-border: #4b5563;
	--sidebar-ring: #0891b2;
	/* Prayer text color tokens */
	--prayer-name-color: var(--foreground);
	--prayer-time-color: var(--foreground);
	--prayer-countdown-color: var(--muted-foreground);
	/* Prayer card height tokens */
	--prayer-card-h-next-xs: 2.25rem;
	--prayer-card-h-next-sm: 2.5rem;
	--prayer-card-h-next-md: 2.75rem;
	--prayer-card-h-next-lg: 3.25rem;
	--prayer-card-h-item-xs: 2rem;
	--prayer-card-h-item-sm: 2.25rem;
	--prayer-card-h-item-md: 2.5rem;
	--prayer-card-h-item-lg: 3rem;
}

.dark {
	/* Kept same values for consistent dark theme */
	--background: #1a1a1a;
	--foreground: #ffffff;
	--card: #111827;
	--card-foreground: #f9fafb;
	--popover: #111827;
	--popover-foreground: #ffffff;
	--primary: #0891b2;
	--primary-foreground: #ffffff;
	--secondary: #4b5563;
	--secondary-foreground: #f9fafb;
	--muted: #111827;
	--muted-foreground: #4b5563;
	--accent: #dc2626;
	--accent-foreground: #ffffff;
	--destructive: #dc2626;
	--destructive-foreground: #ffffff;
	--border: #4b5563;
	--input: #1f2937;
	--ring: #0891b2;
	--chart-1: #0891b2;
	--chart-2: #dc2626;
	--chart-3: #f59e0b;
	--chart-4: #4b5563;
	--chart-5: #ffffff;
	--sidebar: #111827;
	--sidebar-foreground: #f9fafb;
	--sidebar-primary: #0891b2;
	--sidebar-primary-foreground: #ffffff;
	--sidebar-accent: #dc2626;
	--sidebar-accent-foreground: #ffffff;
	--sidebar-border: #4b5563;
	--sidebar-ring: #0891b2;
	/* Prayer text color tokens */
	--prayer-name-color: var(--foreground);
	--prayer-time-color: var(--foreground);
	--prayer-countdown-color: var(--muted-foreground);
	/* Keep same height tokens in dark */
	--prayer-card-h-next-xs: 2.25rem;
	--prayer-card-h-next-sm: 2.5rem;
	--prayer-card-h-next-md: 2.75rem;
	--prayer-card-h-next-lg: 3.25rem;
	--prayer-card-h-item-xs: 2rem;
	--prayer-card-h-item-sm: 2.25rem;
	--prayer-card-h-item-md: 2.5rem;
	--prayer-card-h-item-lg: 3rem;
}
`;

const STYLE_ID = "react-prayer-widget-styles";

/**
 * Injects ALL CSS into the document (variables + fonts)
 * Safe to call multiple times (idempotent)
 * Can be called at any time - handles DOM readiness internally
 */
function injectStyles() {
	// Only run in browser environment
	if (typeof document === "undefined") {
		return;
	}

	// Check if styles are already injected (idempotent)
	if (document.getElementById(STYLE_ID)) {
		return;
	}

	// Ensure document.head exists (should always be true, but safety check)
	if (!document.head) {
		return;
	}

	// Generate font-face CSS lazily (only in browser, uses getAssetUrl)
	const fontFaceCSS = generateFontFaceCSS();

	// Combine CSS variables + font-face declarations
	const fullCSS = `${CSS_VARIABLES}

/* Font-face declarations - fonts are loaded from package's public folder */
${fontFaceCSS}`;

	// Create and inject style element with FULL CSS
	const style = document.createElement("style");
	style.id = STYLE_ID;
	style.textContent = fullCSS;
	document.head.appendChild(style);
}

// Auto-inject on module load (side effect)
// This ensures CSS is automatically included when the package is imported
// Works in all environments: Next.js SSR, Vite, CRA, Remix, etc.
if (typeof window !== "undefined") {
	// Strategy 1: Try immediate injection (works if DOM is ready)
	injectStyles();

	// Strategy 2: Handle DOM loading states
	if (typeof document !== "undefined") {
		if (document.readyState === "loading") {
			// DOM is still loading, wait for it
			document.addEventListener("DOMContentLoaded", injectStyles, { once: true });
		} else if (document.readyState === "interactive" || document.readyState === "complete") {
			// DOM is ready, inject immediately
			injectStyles();
		}
	}

	// Strategy 3: Use requestAnimationFrame for next paint (handles React hydration)
	// This ensures CSS is injected before React paints, preventing FOUC
	if (typeof requestAnimationFrame !== "undefined") {
		requestAnimationFrame(injectStyles);
	}

	// Strategy 4: Final fallback for edge cases (e.g., dynamic imports, code splitting)
	if (typeof setTimeout !== "undefined") {
		setTimeout(injectStyles, 0);
	}
}

// Also export for manual injection if needed
export { injectStyles };
